import { isSameDay, parse } from "date-fns";
import { useMemo } from "react";
import { captureException } from "../../../exceptions";
import { dateFormat } from "../Shared";
import useGetAccountGroupsPerformancesV4 from "./useGetAccountGroupsPerformancesV4";
import useGetAccountGroupsV4 from "./useGetAccountGroupsV4";
import useLastDateForCustodianData from "./useLastDateForCustodianData";
import useManagedAccountCustodiansV4 from "./useManagedAccountCustodiansV4";
import useMatchPerformanceGroupV4 from "./useMatchPerformanceGroupV4";
export default function useAccountBalanceV4(clientId, accountId, auth) {
    const performanceGroupId = useMatchPerformanceGroupV4(accountId, auth);
    const summary = useManagedAccountCustodiansV4(performanceGroupId.data ?? null, "30D", null, clientId, auth);
    const accountGroupsReqBody = {
        dateRanges: null,
        feeMethod: "grossOfFees",
        returnMethod: "cumulative",
    };
    const accountGroups = useGetAccountGroupsV4(auth);
    const accountGroupsPerformances = useGetAccountGroupsPerformancesV4(clientId, accountGroupsReqBody, auth);
    const lastDate = useLastDateForCustodianData(clientId, auth);
    return useMemo(() => {
        if (accountGroups.isLoading || accountGroups.hasError) {
            return accountGroups;
        }
        if (accountGroupsPerformances.isLoading ||
            accountGroupsPerformances.hasError) {
            return accountGroupsPerformances;
        }
        if (lastDate.isLoading || lastDate.hasError) {
            return lastDate;
        }
        const group = accountGroups.data.find((ag) => ag.virtualAccountIds.length === 1 &&
            ag.virtualAccountIds[0] === accountId);
        if (group === undefined) {
            const err = new Error("Account group does not exist for account");
            captureException(err, "useAccountBalanceV4.ts", {
                extra: {
                    file: "useAccountBalanceV4.ts",
                    ClientId: clientId,
                    accountId: accountId,
                },
            });
            return {
                data: undefined,
                isLoading: false,
                hasError: true,
                error: err,
            };
        }
        const performanceGroup = accountGroupsPerformances.data.find((ag) => ag.accountGroupId === group.accountGroupId);
        if (performanceGroup === undefined) {
            const err = new Error("Account group performance does not exist for account");
            captureException(err, "useAccountBalanceV4.ts", {
                extra: {
                    file: "useAccountBalanceV4.ts",
                    ClientId: clientId,
                    accountId: accountId,
                },
            });
            return {
                data: undefined,
                isLoading: false,
                hasError: true,
                error: err,
            };
        }
        // cannot determine a balance bc we don't have the available data even though there is an account group
        if (performanceGroup.balanceSummary === undefined ||
            performanceGroup.balanceSummary.length === 0) {
            return {
                data: null,
                isLoading: false,
                hasError: false,
            };
        }
        // these hook isLoading and hasError checks need to come after the account group look-up code above bc balance can be loading indefinitely but we can deduce a balance unavailable state by looking at the balanceSummary from the accountId's account group
        if (performanceGroupId.isLoading || performanceGroupId.hasError) {
            return performanceGroupId;
        }
        if (summary.isLoading || summary.hasError) {
            return summary;
        }
        const comparisonDate = lastDate.data ?? new Date();
        const balance = summary.data.data.find((b) => isSameDay(parse(b.endDate, dateFormat, new Date()), comparisonDate));
        return {
            data: balance?.balanceOnEndDay ?? null,
            isLoading: false,
            hasError: false,
        };
    }, [
        performanceGroupId,
        summary,
        accountGroups,
        accountGroupsPerformances,
        accountId,
        clientId,
        lastDate,
    ]);
}
//# sourceMappingURL=useAccountBalanceV4.js.map